import React, { useMemo, useState } from 'react';
import {
  Send,
  Inbox,
  Trash2,
  Users,
  Mail,
  MailOpen,
  CheckSquare,
  Square,
  AlertTriangle,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { canSendMessages, canDeleteMessages } from '../../utils/hierarchy';

/**
 * Messages from authority to agents.
 *
 * ONE component for both sides of it, because they are two views of the same
 * records and keeping them in one file is what stops them drifting apart --
 * an inbox that shows something different from what the sender believes they
 * sent is worse than no inbox.
 *
 *   Compose   for admin, management, branch managers, recovery department
 *   Inbox     for everybody, showing the copies addressed to them
 *   Clean up  for administrators only: by month, in bulk, or one at a time
 *
 * Who may write to whom is decided in utils/hierarchy and enforced again when
 * the message is sent, so the screen offering a name is a convenience rather
 * than the rule itself.
 */

const PAGE = 25;

export const MessageCentre: React.FC = () => {
  const {
    currentUser,
    myMessages,
    messages,
    messageRecipients,
    sendStaffMessage,
    markMessageRead,
    deleteStaffMessages,
  } = useSRMS();

  const maySend = canSendMessages(currentUser);
  const mayDelete = canDeleteMessages(currentUser);

  const [tab, setTab] = useState<'inbox' | 'compose' | 'manage'>(
    maySend ? 'compose' : 'inbox'
  );

  // --- compose ---------------------------------------------------------
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState('');
  const [search, setSearch] = useState('');

  const visibleRecipients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return messageRecipients;
    return messageRecipients.filter(
      (u) =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.agentId || '').toLowerCase().includes(q) ||
        (u.branch || '').toLowerCase().includes(q)
    );
  }, [messageRecipients, search]);

  const keyOf = (u: { agentId?: string; id: string }) => u.agentId || u.id;
  const allVisibleChosen =
    visibleRecipients.length > 0 && visibleRecipients.every((u) => chosen.includes(keyOf(u)));

  const toggleOne = (k: string) =>
    setChosen((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const toggleAllVisible = () => {
    const keys = visibleRecipients.map(keyOf);
    setChosen((prev) =>
      allVisibleChosen ? prev.filter((k) => !keys.includes(k)) : Array.from(new Set([...prev, ...keys]))
    );
  };

  const doSend = async () => {
    setSending(true);
    setNote('');
    try {
      const res = await sendStaffMessage(chosen, subject, body);
      setNote(res.message);
      if (res.success) {
        setSubject('');
        setBody('');
        setChosen([]);
      }
    } finally {
      setSending(false);
    }
  };

  // --- inbox -----------------------------------------------------------
  const [inboxPage, setInboxPage] = useState(1);
  const inboxPages = Math.max(1, Math.ceil(myMessages.length / PAGE));
  const inboxRows = myMessages.slice((inboxPage - 1) * PAGE, inboxPage * PAGE);

  // --- manage (admin) --------------------------------------------------
  const [picked, setPicked] = useState<string[]>([]);
  const [month, setMonth] = useState('');

  const months = useMemo(() => {
    const set = new Set<string>();
    (messages || []).forEach((m) => {
      const mm = m.month || (m.sentAt || '').slice(0, 7);
      if (mm) set.add(mm);
    });
    return Array.from(set).sort().reverse();
  }, [messages]);

  const monthLabel = (m: string) => {
    const d = new Date(`${m}-01T00:00:00`);
    return isNaN(d.getTime())
      ? m
      : d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  const countInMonth = (m: string) =>
    (messages || []).filter((x) => (x.month || (x.sentAt || '').slice(0, 7)) === m).length;

  const removeChosen = async () => {
    if (picked.length === 0) return;
    if (
      !window.confirm(
        `Delete ${picked.length} message(s)? This removes them from the agents' inboxes and cannot be undone.`
      )
    )
      return;
    const res = await deleteStaffMessages(picked);
    setNote(res.message);
    if (res.success) setPicked([]);
  };

  const removeMonth = async () => {
    if (!month) return;
    const n = countInMonth(month);
    if (
      !window.confirm(
        `Delete all ${n} message(s) from ${monthLabel(month)}?\n\nThis removes them from every agent's inbox and cannot be undone.`
      )
    )
      return;
    const res = await deleteStaffMessages([], month);
    setNote(res.message);
    if (res.success) setMonth('');
  };

  const when = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime())
      ? iso
      : d.toLocaleString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
  };

  return (
    <div className="space-y-4 text-xs">
      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
        <button
          type="button"
          onClick={() => setTab('inbox')}
          className={`px-3 py-1.5 rounded-lg font-bold transition cursor-pointer flex items-center gap-1.5 ${
            tab === 'inbox'
              ? 'bg-blue-600 text-white'
              : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
          }`}
        >
          <Inbox className="w-3.5 h-3.5" />
          <span>My inbox ({myMessages.length})</span>
        </button>

        {maySend && (
          <button
            type="button"
            onClick={() => setTab('compose')}
            className={`px-3 py-1.5 rounded-lg font-bold transition cursor-pointer flex items-center gap-1.5 ${
              tab === 'compose'
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Send className="w-3.5 h-3.5" />
            <span>Send a message</span>
          </button>
        )}

        {mayDelete && (
          <button
            type="button"
            onClick={() => setTab('manage')}
            className={`px-3 py-1.5 rounded-lg font-bold transition cursor-pointer flex items-center gap-1.5 ${
              tab === 'manage'
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>All messages ({messages.length})</span>
          </button>
        )}
      </div>

      {note && (
        <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg font-bold text-blue-900">
          {note}
        </div>
      )}

      {/* ---------------- INBOX ---------------- */}
      {tab === 'inbox' && (
        <div className="space-y-2.5">
          {myMessages.length === 0 ? (
            <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
              <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 font-medium">No messages.</p>
            </div>
          ) : (
            <>
              {inboxRows.map((m) => (
                <div
                  key={m.id}
                  onClick={() => !m.read && markMessageRead(m.id)}
                  className={`p-3.5 rounded-xl border space-y-1.5 transition ${
                    m.read
                      ? 'bg-white border-slate-200'
                      : 'bg-blue-50/60 border-blue-200 cursor-pointer'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {m.read ? (
                        <MailOpen className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      ) : (
                        <Mail className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                      )}
                      <span
                        className={`truncate ${
                          m.read ? 'font-semibold text-slate-700' : 'font-bold text-slate-900'
                        }`}
                      >
                        {m.subject}
                      </span>
                    </div>
                    <span className="text-[10px] font-mono text-slate-400 shrink-0">
                      {when(m.sentAt)}
                    </span>
                  </div>
                  <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{m.body}</p>
                  <p className="text-[11px] text-slate-500">
                    From <strong>{m.senderName}</strong> ({m.senderRole.replace(/_/g, ' ')})
                    {!m.read && <span className="ml-2 text-blue-700 font-bold">• press to mark read</span>}
                  </p>
                </div>
              ))}

              {inboxPages > 1 && (
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setInboxPage((p) => Math.max(1, p - 1))}
                    disabled={inboxPage <= 1}
                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg font-bold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition"
                  >
                    Previous
                  </button>
                  <span className="text-slate-600 font-semibold">
                    Page {inboxPage} of {inboxPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setInboxPage((p) => Math.min(inboxPages, p + 1))}
                    disabled={inboxPage >= inboxPages}
                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg font-bold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ---------------- COMPOSE ---------------- */}
      {tab === 'compose' && maySend && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recipients */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="font-bold text-slate-800 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-blue-600" />
                Agents ({chosen.length} chosen)
              </span>
              <button
                type="button"
                onClick={toggleAllVisible}
                disabled={visibleRecipients.length === 0}
                className="px-2.5 py-1 rounded-lg bg-white border border-slate-300 hover:bg-slate-100 font-bold text-slate-700 disabled:opacity-40 transition cursor-pointer"
              >
                {allVisibleChosen ? 'Clear these' : 'Select all shown'}
              </button>
            </div>

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find by name, agent code or branch…"
              className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />

            {messageRecipients.length === 0 ? (
              <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-900">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  There are no agents you can message. A branch manager reaches their own
                  branches, and a recovery department user their own bank — so if this is
                  unexpected, check the branch or bank on your profile.
                </span>
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto space-y-1 bg-white border border-slate-200 rounded-lg p-2">
                {visibleRecipients.map((u) => {
                  const k = keyOf(u);
                  const on = chosen.includes(k);
                  return (
                    <label
                      key={u.id}
                      className="flex items-center gap-2 px-1.5 py-1 rounded-md hover:bg-slate-50 cursor-pointer"
                    >
                      {on ? (
                        <CheckSquare className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                      ) : (
                        <Square className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                      )}
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggleOne(k)}
                        className="hidden"
                      />
                      <span className="truncate text-slate-800 font-medium">{u.name}</span>
                      <span className="text-[10px] font-mono text-slate-400 shrink-0">
                        {u.agentId || '—'}
                      </span>
                      {u.branch && (
                        <span className="text-[10px] text-slate-400 truncate">· {u.branch}</span>
                      )}
                    </label>
                  );
                })}
                {visibleRecipients.length === 0 && (
                  <p className="text-slate-400 px-1.5 py-2">Nobody matches that search.</p>
                )}
              </div>
            )}
          </div>

          {/* The message */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-2 font-semibold focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
            <textarea
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write the message…"
              className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-2 leading-relaxed focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-slate-500">
                {chosen.length === 0
                  ? 'Choose who this goes to.'
                  : `Goes to ${chosen.length} agent${chosen.length === 1 ? '' : 's'}.`}
              </span>
              <button
                type="button"
                onClick={doSend}
                disabled={sending || chosen.length === 0 || !body.trim()}
                className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold flex items-center gap-1.5 transition cursor-pointer"
              >
                <Send className="w-3.5 h-3.5" />
                <span>{sending ? 'Sending…' : 'Send'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- MANAGE (admin) ---------------- */}
      {tab === 'manage' && mayDelete && (
        <div className="space-y-3">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
            <span className="font-bold text-slate-800">Clear a whole month</span>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
              >
                <option value="">Choose a month…</option>
                {months.map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)} — {countInMonth(m)} message(s)
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={removeMonth}
                disabled={!month}
                className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold transition cursor-pointer"
              >
                Delete that month
              </button>
            </div>
            <p className="text-[11px] text-slate-400">
              This removes the messages from the agents' inboxes as well. It cannot be undone.
            </p>
          </div>

          <div className="flex items-center justify-between gap-2">
            <span className="font-bold text-slate-700">
              {picked.length > 0 ? `${picked.length} selected` : 'Select messages to delete'}
            </span>
            {picked.length > 0 && (
              <button
                type="button"
                onClick={removeChosen}
                className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-bold transition cursor-pointer"
              >
                Delete selected ({picked.length})
              </button>
            )}
          </div>

          <div className="space-y-2 max-h-[28rem] overflow-y-auto">
            {messages.length === 0 && (
              <p className="p-6 text-center text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                No messages have been sent yet.
              </p>
            )}
            {messages.map((m) => (
              <label
                key={m.id}
                className="flex items-start gap-2.5 p-3 bg-white border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={picked.includes(m.id)}
                  onChange={() =>
                    setPicked((prev) =>
                      prev.includes(m.id) ? prev.filter((x) => x !== m.id) : [...prev, m.id]
                    )
                  }
                  className="mt-0.5 rounded cursor-pointer"
                />
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-slate-900 truncate">{m.subject}</span>
                    <span className="text-[10px] font-mono text-slate-400 shrink-0">
                      {when(m.sentAt)}
                    </span>
                  </div>
                  <p className="text-slate-600 truncate">{m.body}</p>
                  <p className="text-[11px] text-slate-400">
                    {m.senderName} → <strong>{m.recipientName}</strong>
                    {m.read ? ' · read' : ' · unread'}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageCentre;
